import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Alert,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField
} from '@mui/material';
import { API_URL } from '../../config/constants';
import { useAuth } from '../../context/AuthContext';
import './SubscriptionStatus.css';

const SubscriptionStatus = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelOtherText, setCancelOtherText] = useState('');

  // Fixed reason categories keep the data analysable over time. "Other"
  // captures free-text — we send both to the server.
  const CANCEL_REASONS = [
    { value: 'too_expensive', label: 'Too expensive' },
    { value: 'missing_feature', label: 'Missing a feature I need' },
    { value: 'switching_product', label: 'Switching to a different product' },
    { value: 'not_enough_value', label: "Didn't get enough value from it" },
    { value: 'too_complicated', label: 'The product is too complicated' },
    { value: 'temporary_need', label: 'I only needed it temporarily' },
    { value: 'other', label: 'Other (please explain)' }
  ];

  const closeCancelDialog = () => {
    if (actionLoading) return;
    setCancelDialogOpen(false);
    setCancelReason('');
    setCancelOtherText('');
  };

  // Skip for dean.omeara@live.co.uk
  const isSpecialUser = user && user.email === 'dean.omeara@live.co.uk';

  useEffect(() => {
    if (isSpecialUser) {
      setLoading(false);
      return;
    }

    const fetchSubscriptionStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('Authentication required');
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_URL}/stripe/subscription-status`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();
        
        if (data.success) {
          setSubscription(data.subscription);
        } else {
          setError(data.message || 'Failed to fetch subscription details');
        }
      } catch (err) {
        console.error('Error fetching subscription details:', err);
        setError('Failed to fetch subscription details');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionStatus();
  }, [isSpecialUser, user]);

  const handleCancelSubscription = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await fetch(`${API_URL}/stripe/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reason: cancelReason,
          reasonDetails: cancelReason === 'other' ? cancelOtherText.trim() : ''
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Update the subscription status to 'canceled' and set cancelAtPeriodEnd to true
        setSubscription({
          ...subscription,
          status: data.subscription.status,
          cancelAtPeriodEnd: data.subscription.cancelAtPeriodEnd
        });
        closeCancelDialog();

        // Show a success message
        setSuccessMessage('Your subscription has been canceled. You will have access until the end of your current billing period.');
      } else {
        setError(data.message || 'Failed to cancel subscription');
      }
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError('Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResumeSubscription = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await fetch(`${API_URL}/stripe/resume-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        // Update the subscription status and cancelAtPeriodEnd flag
        setSubscription({
          ...subscription,
          status: data.subscription.status,
          cancelAtPeriodEnd: data.subscription.cancelAtPeriodEnd
        });
        setResumeDialogOpen(false);
        
        // Show a success message
        setSuccessMessage('Your subscription has been resumed successfully.');
      } else {
        // Check if subscription is fully cancelled and needs new subscription
        if (data.requiresNewSubscription) {
          setResumeDialogOpen(false);
          setError('Your subscription is fully cancelled. Please create a new subscription.');
          // Optionally redirect to subscription page
          setTimeout(() => {
            navigate('/subscription');
          }, 2000);
        } else {
          setError(data.message || 'Failed to resume subscription');
        }
      }
    } catch (err) {
      console.error('Error resuming subscription:', err);
      setError('Failed to resume subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageSubscription = () => {
    navigate('/subscription');
  };
  
  const handleUpgradeSubscription = () => {
    navigate('/subscription', { state: { action: 'upgrade' } });
  };

  const getStatusColor = (status, cancelAtPeriodEnd) => {
    if ((status === 'active' || status === 'trialing') && cancelAtPeriodEnd) {
      return 'warning';
    }
    
    switch (status) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'info';
      case 'canceled':
        return 'error';
      case 'incomplete':
      case 'incomplete_expired':
        return 'warning';
      case 'past_due':
        return 'warning';
      case 'unpaid':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status, cancelAtPeriodEnd) => {
    if ((status === 'active' || status === 'trialing') && cancelAtPeriodEnd) {
      return 'Canceling';
    }
    
    switch (status) {
      case 'active':
        return 'Active';
      case 'trialing':
        return 'Trial';
      case 'canceled':
        return 'Canceled';
      case 'incomplete':
        return 'Incomplete';
      case 'incomplete_expired':
        return 'Payment Failed';
      case 'past_due':
        return 'Past Due';
      case 'unpaid':
        return 'Unpaid';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Card className="subscription-card">
        <CardContent className="subscription-card-content" sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </CardContent>
      </Card>
    );
  }

  if (isSpecialUser) {
    return (
      <Card sx={{ mb: 2, bgcolor: 'background.paper' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Subscription Status
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Chip 
              label="Enterprise" 
              color="success" 
              size="small" 
              sx={{ mr: 1 }} 
            />
            <Typography variant="body2">
              Special admin account with unlimited access
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ mb: 2, bgcolor: 'background.paper' }}>
        <CardContent>
          <Alert severity="error">{error}</Alert>
          <Button 
            variant="outlined" 
            color="primary" 
            sx={{ mt: 2 }}
            onClick={handleManageSubscription}
          >
            Manage Subscription
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!subscription) {
    return (
      <Card sx={{ mb: 2, bgcolor: 'background.paper' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            No Active Subscription
          </Typography>
          <Typography variant="body2" paragraph>
            You don't have an active subscription. Subscribe to continue using LeaveWise.
          </Typography>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleManageSubscription}
          >
            Subscribe Now
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }}
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}
      <Card className="subscription-card">
        <CardContent className="subscription-card-content">
          <Box className="subscription-header">
            <Typography variant="h6">
              Subscription Status
            </Typography>
            <Chip 
              label={getStatusText(subscription.status, subscription.cancelAtPeriodEnd)} 
              color={getStatusColor(subscription.status, subscription.cancelAtPeriodEnd)} 
              size="small" 
            />
          </Box>
          
          <Divider sx={{ mb: 1.5 }} />
          
          <Box className="subscription-details">
            <Box className="subscription-detail-row">
              <Typography variant="body2" className="subscription-label">Plan:</Typography>
              <Typography variant="body2" className="subscription-value" sx={{ textTransform: 'capitalize' }}>
                {subscription.plan}
              </Typography>
            </Box>
            
            {subscription.currentPeriodEnd && (
              <Box className="subscription-detail-row">
                <Typography variant="body2" className="subscription-label">
                  {subscription.status === 'trialing' ? 'Trial ends:' : 'Next billing date:'}
                </Typography>
                <Typography variant="body2" className="subscription-value">
                  {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                </Typography>
              </Box>
            )}
            
            <Box className="subscription-detail-row">
              <Typography variant="body2" className="subscription-label">Team size limit:</Typography>
              <Typography variant="body2" className="subscription-value">
                {subscription.plan === 'small' ? '5 members' : 
                 subscription.plan === 'medium' ? '12 members' : 
                 subscription.plan === 'large' ? '20 members' : 
                 subscription.plan === 'enterprise' ? '100 members' : 'Unknown'}
              </Typography>
            </Box>
          </Box>
          
          <Box className="subscription-actions">
            {/* Upgrade button - always show for active subscriptions except enterprise */}
            {(subscription.status === 'active' || subscription.status === 'trialing') && 
             subscription.plan !== 'enterprise' && (
              <Button 
                variant="contained" 
                color="primary" 
                size="small"
                onClick={handleUpgradeSubscription}
              >
                Upgrade Plan
              </Button>
            )}
            
            {/* Cancel/Resume buttons */}
            {(subscription.status === 'active' || subscription.status === 'trialing') && !subscription.cancelAtPeriodEnd ? (
              <Button 
                variant="outlined" 
                color="error" 
                size="small"
                onClick={() => setCancelDialogOpen(true)}
              >
                Cancel Subscription
              </Button>
            ) : (subscription.status === 'active' || subscription.status === 'trialing') && subscription.cancelAtPeriodEnd ? (
              <Button 
                variant="outlined" 
                color="primary" 
                size="small"
                onClick={() => setResumeDialogOpen(true)}
              >
                Resume Subscription
              </Button>
            ) : subscription.status === 'canceled' ? (
              <Button 
                variant="contained" 
                color="primary" 
                size="small"
                onClick={handleManageSubscription}
              >
                Start New Subscription
              </Button>
            ) : (
              <Button 
                variant="contained" 
                color="primary" 
                size="small"
                onClick={handleManageSubscription}
              >
                Update Payment Method
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
      
      {/* Cancel Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={closeCancelDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Sorry to see you go! You will still have access until the end of your current billing period.
            Before you leave, please tell us why — it genuinely helps us improve LeaveWise.
          </DialogContentText>
          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <RadioGroup
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            >
              {CANCEL_REASONS.map(r => (
                <FormControlLabel
                  key={r.value}
                  value={r.value}
                  control={<Radio disabled={actionLoading} />}
                  label={r.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
          {cancelReason === 'other' && (
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={5}
              placeholder="Tell us a bit more — optional but really helpful."
              value={cancelOtherText}
              onChange={(e) => setCancelOtherText(e.target.value.slice(0, 500))}
              disabled={actionLoading}
              sx={{ mt: 2 }}
              inputProps={{ maxLength: 500 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeCancelDialog}
            disabled={actionLoading}
          >
            No, Keep It
          </Button>
          <Button
            onClick={handleCancelSubscription}
            color="error"
            disabled={actionLoading || !cancelReason}
            startIcon={actionLoading ? <CircularProgress size={20} /> : null}
          >
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Resume Dialog */}
      <Dialog
        open={resumeDialogOpen}
        onClose={() => !actionLoading && setResumeDialogOpen(false)}
      >
        <DialogTitle>Resume Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Would you like to resume your subscription? You will be charged at the beginning of the next billing period.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setResumeDialogOpen(false)} 
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleResumeSubscription} 
            color="primary" 
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : null}
          >
            Resume Subscription
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubscriptionStatus;
